import { useEffect, useState } from "react";
import { CalendarX } from "lucide-react";
import api from "@/lib/api";

/**
 * Grey banner shown when the active site is CLOSED today (Public Holiday,
 * ad-hoc closure, or a non-working weekday per the site's operating
 * calendar). Silent when the site is operating.
 *
 * Consumers pass the active `siteId`. When `siteId` is falsy (e.g. "All
 * sites" mode) the banner does not render.
 */
export default function ClosedTodayBanner({ siteId }) {
  const [state, setState] = useState(null);   // { closed, closure }

  useEffect(() => {
    if (!siteId) { setState(null); return; }
    const today = new Date().toISOString().slice(0, 10);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/sites/${siteId}/is-operating`, { params: { date: today } });
        if (!cancelled) setState({ closed: !data.operating, closure: data.closure });
      } catch {
        if (!cancelled) setState(null);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  if (!state || !state.closed) return null;
  const type = state.closure?.type || "CL";
  const reason = state.closure?.reason || "";
  const label = type === "PH" ? "Public Holiday" : "Closed";
  return (
    <div
      className="vb-card p-3 flex items-center gap-3"
      data-testid="closed-today-banner"
      style={{
        borderColor: "rgba(120,120,120,0.35)",
        background: "rgba(120,120,120,0.08)",
      }}>
      <span
        style={{
          background: type === "PH" ? "#2563eb" : "#6b7280",
          color: "white", padding: "2px 8px", borderRadius: 4,
          fontWeight: 700, fontSize: 11, letterSpacing: 0.5,
        }}>{type}</span>
      <CalendarX size={16} className="opacity-60" />
      <div className="text-sm">
        <strong>Site is closed today — {label}.</strong>{" "}
        <span className="opacity-70">
          {reason || "Non-working day."} Today will not count as a missing
          entry in completeness reports.
        </span>
      </div>
    </div>
  );
}
